import { config } from "./config";
import { Ajax } from "../../core/Ajax";
import { Tools } from "../../utilities/Tools";
import { Preloader } from "../Preloader";
import { viewHeaderTpl } from "./view/view-header.tpl";
import { viewBodySidebarTpl } from "./view/view-body-sidebar.tpl";
import { viewBodyHeaderTpl } from "./view/view-body-header.tpl";
import { viewBodyContentTpl } from "./view/view-body-content.tpl";

class ProjectsCalendar {
  constructor(calendar, settings = {}) {
    this.preloader = new Preloader(calendar);
    this.calendar = calendar;
    this._settings = Object.assign(
      config,
      Tools.parseStringValuesInObjectLiteral(
        ((calendar) => {
          const { dataset } = calendar;

          if (dataset) {
            const datasetObject = Object.assign({}, dataset);

            delete datasetObject.toggle;
            return datasetObject;
          } else {
            return {};
          }
        })(calendar)
      ),
      Tools.parseStringValuesInObjectLiteral(settings)
    );

    // Przyciski
    this._buttons = {};

    // Żródła widoku
    this._resources = {};

    // Dane projekty
    this._projectsData = [];

    // Dane daty
    this._viewData = {};

    // Dane .projects-calendar__body-sidebar
    this._viewBodySidebarData = undefined;

    // Dane .projects-calendar__body-header
    this._viewBodyHeaderData = [];

    // Dane .projects-calendar__body-content
    this._viewBodyContentData = undefined;

    // Dane popover
    this.popoverData = new Map();
    this._popoverTimeout = undefined;

    // Stworzenie nowej funkcji z nowo przypisanym this na potrzeby dodawania i usuwania zadarzeń
    this.updateSettingsBind = this.updateSettings.bind(this);
    this.getProjectsPopoverBind = this.getProjectsPopover.bind(this);
    this.mousedownHandlerBind = this.mousedownHandler.bind(this);
    this.mousemoveHandlerBind = this.mousemoveHandler.bind(this);
    this.mouseupHandlerBind = this.mouseupHandler.bind(this);

    //
    this._highlightedColumnFirstIndex = null;
    this._highlightedColumnFirstWidth = null;
    this._highlightedColumnDirection = null;
    this._highlightedColumns = [];
    this._highlightedRow = null;

    // Inicjalizacja
    this.init();
  }

  //
  get buttons() {
    return this._buttons;
  }

  set buttons(buttons) {
    Object.assign(this._buttons, buttons);
  }

  get resources() {
    return this._resources;
  }

  set resources(value) {
    this._resources = value;
  }

  get settings() {
    return this._settings;
  }

  set settings(object) {
    Object.assign(this._settings, object);

    // W przypadku przełączenie w tryb pełnego ekranu renderujemy tylko .projects-calendar__header.
    if (Object.keys(object).length === 1 && object.hasOwnProperty("projectsCalendarFullScreenMode")) {
      this.renderViewHeader();
      this.calculateProjectsCalendarBodyHeight();
      return false;
    }

    //
    this.setProjectsCalendarBodyModifierClass();

    // Przygotowanie danych.
    this.calculateViewData();
    this.calculateViewBodySidebarData();
    this.calculateViewBodyHeaderData();
    this.calculateViewBodyContentData();

    // Po zmianie w obiekcie ustawień następuje ponowne wyrendwerowanie składowych widoku kalendarza.
    this.renderViewHeader();
    this.renderViewBodySidebar();
    this.renderViewBodyHeader();
    this.renderViewBodyContent();

    //
    this.getProjectsData();
  }

  get viewData() {
    return this._viewData;
  }

  set viewData(object) {
    Object.assign(this._viewData, object);
  }

  get viewBodySidebarData() {
    return this._viewBodySidebarData;
  }

  set viewBodySidebarData(array) {
    this._viewBodySidebarData = array;
  }

  get viewBodyHeaderData() {
    return this._viewBodyHeaderData;
  }

  set viewBodyHeaderData(array) {
    this._viewBodyHeaderData = array;
  }

  get viewBodyContentData() {
    return this._viewBodyContentData;
  }

  set viewBodyContentData(value) {
    this._viewBodyContentData = value;
  }

  get projectsData() {
    return this._projectsData;
  }

  set projectsData(object) {
    this._projectsData = object;

    this.renderProjectsOnTheCalendar();
  }

  get popoverTimeout() {
    return this._popoverTimeout;
  }

  set popoverTimeout(value) {
    this._popoverTimeout = value;
  }

  get highlightedColumnFirstIndex() {
    return this._highlightedColumnFirstIndex;
  }

  set highlightedColumnFirstIndex(value) {
    this._highlightedColumnFirstIndex = value;
  }

  get highlightedColumnFirstWidth() {
    return this._highlightedColumnFirstWidth;
  }

  set highlightedColumnFirstWidth(value) {
    this._highlightedColumnFirstWidth = value;
  }

  get highlightedColumnDirection() {
    return this._highlightedColumnDirection;
  }

  set highlightedColumnDirection(value) {
    this._highlightedColumnDirection = value;
  }

  get highlightedColumns() {
    return this._highlightedColumns;
  }

  set highlightedColumns(array) {
    if (JSON.stringify(this.highlightedColumns) !== JSON.stringify(array)) {
      this._highlightedColumns = array;

      if (array.length > 0) {
        this.calcWidthOfHighlight();
      }
    }
  }

  get highlightedRow() {
    return this._highlightedRow;
  }

  set highlightedRow(value) {
    this._highlightedRow = value;
  }

  // Dodawanie zdarzeń
  addEvents() {
    document.addEventListener("fullscreenchange", (event) => {
      if (document.fullscreenElement !== null) {
        this.settings = {
          projectsCalendarFullScreenMode: true,
        };
      } else {
        this.settings = {
          projectsCalendarFullScreenMode: false,
        };
      }
    });
  }

  // Dodawanie zdarzeń
  addViewEvents() {
    this.calendar.querySelector(".projects-calendar__body-content").addEventListener("mousedown", this.mousedownHandlerBind);

    this.calendar.querySelector(".projects-calendar__body-content").addEventListener("mouseup", this.mouseupHandlerBind);
  }

  // Dodawanie zdarzeń
  addViewHeaderEvents() {
    const _this = this;

    // Zadarzenia dla dedykowanych przycisków.
    if (Object.keys(this.settings.projectsCalendarCustomButtonsFunctions).length > 0) {
      const buttons = this.settings.projectsCalendarCustomButtonsFunctions;

      for (const buttonsKey in buttons) {
        if (Object.hasOwnProperty.call(this.buttons, buttonsKey)) {
          const fns = buttons[buttonsKey];

          for (const fnsKey in fns) {
            if (Object.hasOwnProperty.call(fns, fnsKey)) {
              const fn = fns[fnsKey];

              if (Boolean(this.calendar.querySelector(".projects-calendar__header-" + buttonsKey))) {
                this.calendar.querySelector(".projects-calendar__header-" + buttonsKey).addEventListener(fnsKey, fn);
              }
            }
          }
        }
      }
    }

    // Zadarzenia dla domyślnych przycisków.
    // Wymagania jQuery, jQuery UI - Datepicker
    $(this.calendar.querySelector(".projects-calendar__header-select"))
      .datepicker({
        changeMonth: true,
        changeYear: true,
        showOn: "button",
        buttonText: ((buttons) => {
          const { icon, text } = buttons.select;

          return (icon !== "" ? `<i class="${icon}"></i>` : "") + (text !== "" ? text : "");
        })(_this.buttons),
        onSelect: function (inputValue) {
          _this.updateSettings({
            target: _this.calendar.querySelector(".projects-calendar__header-select"),
          });
        },
      })
      .next(".ui-datepicker-trigger")
      .addClass("btn btn-primary");

    this.calendar.querySelector(".projects-calendar__header-today").addEventListener("click", this.updateSettingsBind);
    this.calendar.querySelector(".projects-calendar__header-prev").addEventListener("click", this.updateSettingsBind);
    this.calendar.querySelector(".projects-calendar__header-next").addEventListener("click", this.updateSettingsBind);
    this.calendar.querySelector(".projects-calendar__header-fullscreen").addEventListener("click", this.updateSettingsBind);
    this.calendar.querySelector(".projects-calendar__header-view").addEventListener("change", this.updateSettingsBind);

    // Zdarzenia dla zakresu.
    if (this.settings.projectsCalendarView === "timelineRange" || this.settings.projectsCalendarView === "timelineRangeArea") {
      let from = new Date(this.viewData[this.settings.projectsCalendarView].start);
      let to = new Date(this.viewData[this.settings.projectsCalendarView].end);

      // Wymagania jQuery, jQuery UI - Datepicker
      $(this.calendar.querySelector(".projects-calendar__form-from")).datepicker({
        changeMonth: true,
        changeYear: true,
        maxDate: to,
        onSelect: function (inputValue) {
          _this.updateSettings({
            target: _this.calendar.querySelector(".projects-calendar__form-from"),
          });
        },
      });

      $(this.calendar.querySelector(".projects-calendar__form-to")).datepicker({
        changeMonth: true,
        changeYear: true,
        minDate: from,
        onSelect: function (inputValue) {
          _this.updateSettings({
            target: _this.calendar.querySelector(".projects-calendar__form-to"),
          });
        },
      });
    }
  }

  // Dodawanie zdarzeń
  addViewBodyContentEvents() {
    [...this.calendar.querySelectorAll(".projects-calendar__project")].forEach((project, index) => {
      project.addEventListener("mouseenter", (event) => {
        this.mouseenterProject(event.target);
      });

      project.addEventListener("mouseleave", (event) => {
        this.mouseleaveProject(event.target);
      });

      // Calendar popover
      switch (this.settings.projectsCalendarPopoverTrigger) {
        case "click":
          project.addEventListener("click", this.getProjectsPopoverBind);
          break;

        case "mouseenter":
          project.addEventListener("mouseenter", this.getProjectsPopoverBind);
          project.addEventListener("mouseleave", this.getProjectsPopoverBind);
          break;

        default:
          break;
      }
    });
  }

  updateSettings(event) {
    let dateStart = new Date(this.settings.projectsCalendarStart * 1000);
    let dateEnd = new Date(this.settings.projectsCalendarEnd * 1000);
    const dateDiff = Tools.dateDiff(dateStart.toString(), dateEnd.toString());
    const resource = this.resources[this.settings.projectsCalendarView];
    const { days = 0, months = 0, weeks = 0, years = 0 } = resource.duration || {};

    switch (true) {
      case Boolean(event.target.closest(".projects-calendar__header-select")):
        let inputDate = Date.parse(`${event.target.value}T00:00:00`);

        if (this.settings.projectsCalendarView === "timelineRange" || this.settings.projectsCalendarView === "timelineRangeArea") {
          this.settings = {
            projectsCalendarStart: inputDate / 1000,
            projectsCalendarEnd: new Date(inputDate).setMilliseconds(dateDiff.diff) / 1000,
          };
        } else {
          this.settings = {
            projectsCalendarStart: inputDate / 1000,
          };
        }
        break;

      case Boolean(event.target.closest(".projects-calendar__header-today")):
        dateStart = new Date().setHours(0, 0, 0);

        if (this.settings.projectsCalendarView === "timelineRange" || this.settings.projectsCalendarView === "timelineRangeArea") {
          this.settings = {
            projectsCalendarStart: dateStart / 1000,
            projectsCalendarEnd: new Date(dateStart).setMilliseconds(dateDiff.diff) / 1000,
          };
        } else {
          this.settings = {
            projectsCalendarStart: dateStart / 1000,
          };
        }
        break;

      case Boolean(event.target.closest(".projects-calendar__header-prev")):
        if (days > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate() - days).getTime() / 1000,
          };
        } else if (weeks > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate() - weeks * 7).getTime() / 1000,
          };
        } else if (months > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear(), dateStart.getMonth() - months, dateStart.getDate()).getTime() / 1000,
          };
        } else if (years > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear() - years, dateStart.getMonth(), dateStart.getDate() + days).getTime() / 1000,
          };
        } else {
          dateEnd = new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate() - 1, 23, 59, 59);

          this.settings = {
            projectsCalendarStart: new Date(dateEnd).setMilliseconds(-dateDiff.diff) / 1000,
            projectsCalendarEnd: dateEnd.getTime() / 1000,
          };
        }
        break;

      case Boolean(event.target.closest(".projects-calendar__header-next")):
        if (days > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate() + days).getTime() / 1000,
          };
        } else if (weeks > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate() + weeks * 7).getTime() / 1000,
          };
        } else if (months > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear(), dateStart.getMonth() + months, dateStart.getDate()).getTime() / 1000,
          };
        } else if (years > 0) {
          this.settings = {
            projectsCalendarStart: new Date(dateStart.getFullYear() + years, dateStart.getMonth(), dateStart.getDate() + days).getTime() / 1000,
          };
        } else {
          dateStart = new Date(dateEnd.getFullYear(), dateEnd.getMonth(), dateEnd.getDate() + 1, 0, 0, 0);

          this.settings = {
            projectsCalendarStart: dateStart.getTime() / 1000,
            projectsCalendarEnd: dateStart.setMilliseconds(dateDiff.diff) / 1000,
          };
        }
        break;

      case Boolean(event.target.closest(".projects-calendar__header-fullscreen")):
        if (!document.fullscreenElement) {
          this.calendar.requestFullscreen().then((r) => {});
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen().then((r) => {});
          }
        }
        break;

      case Boolean(event.target.closest(".projects-calendar__header-view")):
        if (event.target.value === "timelineRange" || event.target.value === "timelineRangeArea") {
          this.settings = {
            projectsCalendarView: event.target.value,
            projectsCalendarStart: this.viewData[this.settings.projectsCalendarView].start / 1000,
            projectsCalendarEnd: this.viewData[this.settings.projectsCalendarView].end / 1000,
          };
        } else {
          this.settings = {
            projectsCalendarView: event.target.value,
            projectsCalendarStart: this.viewData[this.settings.projectsCalendarView].start / 1000,
          };
        }
        break;

      case Boolean(event.target.closest(".projects-calendar__form-from")):
        this.settings = {
          projectsCalendarStart: Date.parse(`${event.target.value}T00:00:00`) / 1000,
        };
        break;

      case Boolean(event.target.closest(".projects-calendar__form-to")):
        this.settings = {
          projectsCalendarEnd: Date.parse(`${event.target.value}T23:59:59`) / 1000,
        };
        break;

      default:
        break;
    }
  }

  // Przeliczanie dat względem daty początkowej i końcowej.
  calculateViewData() {
    if (!this.resources.hasOwnProperty(this.settings.projectsCalendarView)) {
      throw new Error(`${this.settings.projectsCalendarView}: nie został zdefiniowany w konfiguracji projectsCalendarResources!`);
    }

    const start = new Date(this.settings.projectsCalendarStart * 1000);
    const end = new Date(this.settings.projectsCalendarEnd * 1000);
    const resource = this.resources[this.settings.projectsCalendarView];
    const { days = 0, months = 0, weeks = 0, years = 0 } = resource.duration || {};
    let timeline = {};

    if (days > 0) {
      timeline = {
        start: (function () {
          return new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
        })(),
        end: (function () {
          return new Date(start.getFullYear(), start.getMonth(), start.getDate() + days).setSeconds(-1);
        })(),
      };
    } else if (weeks > 0) {
      if (weeks === 1) {
        timeline = {
          start: (function () {
            let diff = start.getDay() === 0 ? start.getDate() - 6 : start.getDate() - (start.getDay() - 1);

            return new Date(start.getFullYear(), start.getMonth(), diff).getTime();
          })(),
          end: (function () {
            let diff = start.getDay() === 0 ? start.getDate() : start.getDate() + (7 - start.getDay());

            return new Date(start.getFullYear(), start.getMonth(), diff, 23, 59, 59).getTime();
          })(),
        };
      } else {
        timeline = {
          start: (function () {
            return new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
          })(),
          end: (function () {
            return new Date(start.getFullYear(), start.getMonth(), start.getDate() + weeks * 7).setSeconds(-1);
          })(),
        };
      }
    } else if (months > 0) {
      timeline = {
        start: (function () {
          return new Date(start.getFullYear(), start.getMonth(), 1).getTime();
        })(),
        end: (function () {
          return new Date(start.getFullYear(), start.getMonth() + months, 1).setSeconds(-1);
        })(),
      };
    } else if (years > 0) {
      timeline = {
        start: (function () {
          return new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
        })(),
        end: (function () {
          return new Date(start.getFullYear() + years, start.getMonth(), start.getDate()).setSeconds(-1);
        })(),
      };
    } else {
      timeline = {
        start: (function () {
          return new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
        })(),
        end: (function () {
          return new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59).getTime();
        })(),
      };
    }

    this.viewData = {
      [this.settings.projectsCalendarView]: timeline,
    };
  }

  //
  calculateViewBodySidebarData() {
    let data = undefined;

    switch (this.getResourceType()) {
      case "date":
        data = [];
        let startDate = new Date(this.viewData[this.settings.projectsCalendarView].start);
        let endDate = new Date(this.viewData[this.settings.projectsCalendarView].end);

        while (startDate < endDate) {
          data.push(startDate);
          startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
        }

        break;

      case "area":
        // Zmienna resources jest tworzona w pliku php (modules/projekty/includes/projekty/kalendarz2.inc.php).
        data = resources || [];

        break;

      default:
        break;
    }

    this.viewBodySidebarData = data;
  }

  //
  calculateViewBodyHeaderData() {
    const { slotLabelInterval, slotLabelFormat } = this.settings.projectsCalendarResources[this.settings.projectsCalendarView];
    const start = new Date(this.viewData[this.settings.projectsCalendarView].start);
    const end = new Date(this.viewData[this.settings.projectsCalendarView].end);
    let data = new Map();

    //
    for (const key in slotLabelFormat) {
      if (Object.hasOwnProperty.call(slotLabelFormat, key)) {
        const label = slotLabelFormat[key];

        switch (true) {
          case label.type === "year" && this.getResourceType() === "area": // Rok
            data.set(label.type, {
              format: label.format,
              data: (() => {
                let data = new Map();
                let startWeekday = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                let endWeekday = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                const months = new Set();

                while (startWeekday <= endWeekday) {
                  let startYear = new Date(startWeekday.getFullYear(), 0);
                  let startYearString = startYear.toLocaleDateString("pl-PL", { year: "numeric" });
                  let startMonth = new Date(startWeekday.getFullYear(), startWeekday.getMonth());
                  let startMonthString = startMonth.toLocaleDateString("pl-PL", {
                    year: "numeric",
                    month: "2-digit",
                  });

                  if (data.has(startYearString)) {
                    months.add(startMonthString);

                    data.set(startYearString, {
                      date: startYear,
                      hour: data.get(startYearString).hour + this.getHours(slotLabelInterval, startWeekday).length,
                      weekday: data.get(startYearString).weekday + 1,
                      month: months.size,
                    });
                  } else {
                    data.set(startYearString, {
                      date: startYear,
                      hour: this.getHours(slotLabelInterval, startWeekday).length,
                      weekday: 1,
                      month: 1,
                    });
                  }

                  startWeekday = new Date(startWeekday.getFullYear(), startWeekday.getMonth(), startWeekday.getDate() + 1);
                }

                return data;
              })(),
            });
            break;

          case label.type === "month" && this.getResourceType() === "area": // Miesiąc
            data.set(label.type, {
              format: label.format,
              data: (() => {
                let data = new Map();
                let startWeekday = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                let endWeekday = new Date(end.getFullYear(), end.getMonth(), end.getDate());

                while (startWeekday <= endWeekday) {
                  let startMonth = new Date(startWeekday.getFullYear(), startWeekday.getMonth());
                  let startMonthString = startMonth.toLocaleDateString("pl-PL", {
                    year: "numeric",
                    month: "2-digit",
                  });

                  if (data.has(startMonthString)) {
                    data.set(startMonthString, {
                      date: startMonth,
                      hour: data.get(startMonthString).hour + this.getHours(slotLabelInterval, startWeekday).length,
                      weekday: data.get(startMonthString).weekday + 1,
                    });
                  } else {
                    data.set(startMonthString, {
                      date: startMonth,
                      hour: this.getHours(slotLabelInterval, startWeekday).length,
                      weekday: 1,
                    });
                  }

                  startWeekday = new Date(startWeekday.getFullYear(), startWeekday.getMonth(), startWeekday.getDate() + 1);
                }

                return data;
              })(),
            });
            break;

          case label.type === "weekday" && this.getResourceType() === "area": // Dzień tygodnia
            data.set(label.type, {
              format: label.format,
              data: (() => {
                const data = new Map();
                let startWeekday = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                let endWeekday = new Date(end.getFullYear(), end.getMonth(), end.getDate());

                while (startWeekday <= endWeekday) {
                  let startWeekdayString = startWeekday.toLocaleDateString();

                  data.set(startWeekdayString, {
                    date: startWeekday,
                    hour: this.getHours(slotLabelInterval, startWeekday).length,
                  });
                  startWeekday = new Date(startWeekday.getFullYear(), startWeekday.getMonth(), startWeekday.getDate() + 1);
                }

                return data;
              })(),
            });
            break;

          case label.type === "hour": // Godzina
            data.set(label.type, {
              format: label.format,
              data: (() => {
                // Wyliczenie ilości interwałów godzinowych w zakresie dat (start, end)
                let hours = [];
                let startWeekday = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                let endWeekday = new Date(end.getFullYear(), end.getMonth(), end.getDate());

                //
                switch (this.getResourceType()) {
                  case "date":
                    hours = this.getHours(slotLabelInterval, startWeekday);
                    break;

                  case "area":
                    while (startWeekday <= endWeekday) {
                      const dataHours = this.getHours(slotLabelInterval, startWeekday);

                      hours = hours.concat(dataHours);

                      startWeekday = new Date(startWeekday.getFullYear(), startWeekday.getMonth(), startWeekday.getDate() + 1);
                    }
                    break;

                  default:
                    break;
                }

                return hours;
              })(),
            });
            break;

          default:
            break;
        }
      }
    }

    this.viewBodyHeaderData = data;
  }

  //
  calculateViewBodyContentData() {
    const { slotLabelInterval, slotDuration } = this.settings.projectsCalendarResources[this.settings.projectsCalendarView];
    let start = new Date(this.viewData[this.settings.projectsCalendarView].start);
    const end = new Date(this.viewData[this.settings.projectsCalendarView].end);
    let slotLabelMinutes = Tools.getMinutesFromTimeString(slotLabelInterval);
    let slotDurationMinutes = Tools.getMinutesFromTimeString(slotDuration);
    let columns = [];
    let rows = [];

    switch (this.getResourceType()) {
      case "date":
        // Columns
        if (slotLabelMinutes >= slotDurationMinutes) {
          if (slotLabelMinutes % slotDurationMinutes === 0) {
            columns = this.getHours(slotDuration, start);
          } else {
            columns = this.getHours(slotLabelInterval, start);
          }
        } else {
          columns = this.getHours(slotLabelInterval, start);
        }

        // Rows
        while (start < end) {
          rows.push(start);
          start = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
        }

        break;

      case "area":
        while (start < end) {
          // Columns
          if (slotLabelMinutes >= slotDurationMinutes) {
            if (slotLabelMinutes % slotDurationMinutes === 0) {
              columns = columns.concat(this.getHours(slotDuration, start));
            } else {
              columns = columns.concat(this.getHours(slotLabelInterval, start));
            }
          } else {
            columns = columns.concat(this.getHours(slotLabelInterval, start));
          }

          start = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
        }

        rows = this.viewBodySidebarData;

        break;

      default:
        break;
    }

    this.viewBodyContentData = {
      columns: columns,
      rows: rows,
    };
  }

  getProjectsData() {
    const formData = new FormData(document.getElementById("search"));
    let dayFrom = this.viewData[this.settings.projectsCalendarView].start / 1000;
    let dayTo = this.viewData[this.settings.projectsCalendarView].end / 1000;

    // Zmiana wartości w obiekcie FormData.
    formData.set("ajax_type", "data");
    formData.set("search-dzien-od", dayFrom);
    formData.set("search-dzien-do", dayTo);

    // Pokazanie preloadera
    this.preloader.show();

    // Wysłanie żądania Ajax
    Ajax.sendAjaxRequest(
      `${this.settings.projectsCalendarUrl}?${new URLSearchParams(formData).toString()}`,
      {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      },
      "",
      false
    )
      .then((json) => {
        // Ukrycie preloadera
        this.preloader.hide();

        //
        this.projectsData = json;
      })
      .catch((e) => console.log(e));
  }

  renderViewHeader() {
    this.calendar.querySelector(".projects-calendar__header").innerHTML = viewHeaderTpl(this.settings, this.viewData, this.buttons, this.resources);

    // Dodanie zdarzeń po wyrenderowaniu nagłówka kalendarza.
    this.addViewHeaderEvents();
  }

  renderViewBodySidebar() {
    this.calendar.querySelector(".projects-calendar__body-sidebar").innerHTML = viewBodySidebarTpl(this.getResourceType(), this.viewBodySidebarData);
  }

  renderViewBodyHeader() {
    this.calendar.querySelector(".projects-calendar__body-header").innerHTML = viewBodyHeaderTpl(this.settings, this.viewBodyHeaderData, this.getResource());

    //
    this.calculateProjectsCalendarBodySidebarPaddingTop();
  }

  renderViewBodyContent() {
    this.calendar.querySelector(".projects-calendar__body-content").innerHTML = viewBodyContentTpl(this.getResourceType(), this.viewBodyContentData);
  }

  renderView() {
    this.calendar.insertAdjacentHTML(
      "beforeend",
      (function () {
        return `
                <div class="projects-calendar__header"></div>
                <div class="projects-calendar__body">
                    <div class="projects-calendar__body-sidebar"></div>
                    <div class="projects-calendar__body-main">
                        <div class="projects-calendar__body-header"></div>
                        <div class="projects-calendar__body-content"></div>
                    </div>
                </div>
                `;
      })()
    );

    //
    this.setProjectsCalendarBodyModifierClass();
    this.calculateProjectsCalendarBodyHeight();

    //
    this.addViewEvents();

    //
    this.renderViewHeader();
    this.renderViewBodySidebar();
    this.renderViewBodyHeader();
    this.renderViewBodyContent();

    //
    this.getProjectsData();
  }

  renderProjectsOnTheCalendar() {
    if (this.projectsData.length > 0) {
      switch (this.getResourceType()) {
        case "date":
          this.renderProjectsOnTheCalendarDate();
          break;

        case "area":
          this.renderProjectsOnTheCalendarArea();
          break;

        default:
          break;
      }
    } else {
      switch (this.getResourceType()) {
        case "date":
          this.renderEmptyRowOnTheCalendarDate();
          break;

        case "area":
          this.renderEmptyRowOnTheCalendarArea();
          break;

        default:
          break;
      }
    }

    // Dodanie zdarzeń po wyrenderowaniu projektów.
    this.addViewBodyContentEvents();

    //
    this.calculateProjectsCalendarBodySidebarLiHeight();
  }

  renderEmptyRowOnTheCalendarDate() {
    //
    const { rows } = this.viewBodyContentData;
    let projectsCalendarRowDivLength = rows.length;
    let html = "";

    for (let i = 0; i < projectsCalendarRowDivLength; i++) {
      let resourceId = rows[i].toLocaleDateString();

      html += `<div class="projects-calendar__body-content-row" data-resource-id="${resourceId}"></div>`;
    }

    this.calendar.querySelector(".projects-calendar__body-content").insertAdjacentHTML("beforeend", html);
  }

  renderEmptyRowOnTheCalendarArea() {
    const { rows } = this.viewBodyContentData;
    let html = "";

    for (const key in rows) {
      if (Object.hasOwnProperty.call(rows, key)) {
        let areaId = rows[key].id;

        if (!this.settings.projectsCalendarHideEmptyResources) {
          html += `<div class="projects-calendar__body-content-row" data-resource-id="${areaId}"></div>`;
        }
      }
    }

    this.calendar.querySelector(".projects-calendar__body-content").insertAdjacentHTML("beforeend", html);
  }

  renderProjectsOnTheCalendarDate() {
    const { columns } = this.viewBodyContentData;
    const headerStartDate = columns[0];
    const headerEndDate = columns[columns.length - 1];
    let headerStartMinutes = Tools.getMinutesFromTimeString(headerStartDate.toLocaleTimeString());
    let headerEndMinutes = Tools.getMinutesFromTimeString(headerEndDate.toLocaleTimeString()) + this.getGridInterval();
    const projectsData = this.projectsData;
    let projectsDataLength = projectsData.length;
    let i = 0;
    let projectsMap = new Map();

    while (i < projectsDataLength) {
      const project = projectsData[i];

      let projectStartDate = new Date(Date.parse(project.start.replace(/ /g, "T")));
      const projectEndDate = new Date(Date.parse(project.end.replace(/ /g, "T")));
      let projectStartMinutes = Tools.getMinutesFromTimeString(projectStartDate.toLocaleTimeString());
      let projectEndMinutes = Tools.getMinutesFromTimeString(projectEndDate.toLocaleTimeString());
      let projectResourceId = "";
      let projectClass = "";
      let projectStyle = Tools.getStyleAttribute({
        width: ((projectEndMinutes - projectStartMinutes) * 100) / (headerEndMinutes - headerStartMinutes) + "%",
        "margin-left": ((projectStartMinutes - headerStartMinutes) * 100) / (headerEndMinutes - headerStartMinutes) + "%",
      });
      let projectStyleBackground = (() => {
        if (project.hasOwnProperty("borderColor")) {
          return Tools.getStyleAttribute({
            "background-color": project.color,
            border: `1px solid ${project.borderColor}`,
          });
        } else {
          return Tools.getStyleAttribute({
            "background-color": project.color,
          });
        }
      })();
      let projectTextColor = Tools.getStyleAttribute({
        color: project.textColor,
      });
      let href = this.settings.projectsCalendarPopoverTrigger === "mouseenter" ? `href="${project.url}"` : ``;

      // Składowa projektu rozpoczyna się i kończy w tym samym dniu.
      if (projectStartDate.toLocaleDateString() === projectEndDate.toLocaleDateString()) {
        projectResourceId = projectStartDate.toLocaleDateString();

        // Czas rozpoczęcia dla składowej projektu jest większy równy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest mniejszy równy od czasu zakończenia widoku.
        if (projectStartMinutes >= headerStartMinutes && projectEndMinutes <= headerEndMinutes) {
          projectClass = "projects-calendar__project--start projects-calendar__project--end";
        }

        // Czas rozpoczęcia dla składowej projektu jest mniejszy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest większy od czasu zakończenia widoku.
        if (projectStartMinutes < headerStartMinutes && projectEndMinutes > headerEndMinutes) {
          projectClass = "";
          projectStyle = Tools.getStyleAttribute({
            "margin-left": 0,
          });
        }

        // Czas rozpoczęcia dla składowej projektu jest większy równy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest większy od czasu zakończenia widoku.
        if (projectStartMinutes >= headerStartMinutes && projectEndMinutes > headerEndMinutes) {
          projectClass = "projects-calendar__project--start";
          projectStyle = Tools.getStyleAttribute({
            "margin-left": ((projectStartMinutes - headerStartMinutes) * 100) / (headerEndMinutes - headerStartMinutes) + "%",
          });
        }

        // Czas rozpoczęcia dla składowej projektu jest mniejszy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest mniejszy równy od czasu zakończenia widoku.
        if (projectStartMinutes < headerStartMinutes && projectEndMinutes <= headerEndMinutes) {
          projectClass = "projects-calendar__project--end";
        }

        // Tworzenie składowej projektu.
        projectsMap = Tools.mapSetStringDuplicateKeys(
          projectsMap,
          projectResourceId,
          `<a tabindex="0" ${href} class="projects-calendar__project ${projectClass}" data-id="${project.id}" ${projectStyle}>
                <div class="projects-calendar__project-background shadow-sm" ${projectStyleBackground}></div>
                <div class="projects-calendar__project-description">
                    <span ${projectTextColor}>${project.title.replace(/\r?\n/g, "<br>")}</span>
                </div>
            </a>`
        );
      } else {
        let projectStartDateString = projectStartDate.toLocaleDateString();
        let projectEndDateString = projectEndDate.toLocaleDateString();

        while (projectStartDate < projectEndDate) {
          projectResourceId = projectStartDate.toLocaleDateString();

          switch (projectStartDate.toLocaleDateString()) {
            // Pierwszy element
            case projectStartDateString:
              // Czas rozpoczęcia dla składowej projektu jest większy równy od czasu rozpoczęcia widoku.
              if (projectStartMinutes >= headerStartMinutes) {
                projectClass = "projects-calendar__project--start";
                projectStyle = Tools.getStyleAttribute({
                  "margin-left": ((projectStartMinutes - headerStartMinutes) * 100) / (headerEndMinutes - headerStartMinutes) + "%",
                });
              }

              // Czas rozpoczęcia dla składowej projektu jest mniejszy od czasu rozpoczęcia widoku.
              if (projectStartMinutes < headerStartMinutes) {
                projectClass = "";
                projectStyle = Tools.getStyleAttribute({
                  "margin-left": 0,
                });
              }

              // Czas rozpoczęcia dla składowej projektu jest większy od czasu zakończenia widoku.
              if (projectStartMinutes > headerEndMinutes) {
                projectClass = "";
                projectStyle = Tools.getStyleAttribute({
                  display: "none",
                });
              }

              // Tworzenie składowej projektu.
              projectsMap = Tools.mapSetStringDuplicateKeys(
                projectsMap,
                projectResourceId,
                `<a tabindex="0" ${href} class="projects-calendar__project ${projectClass}" data-id="${project.id}" ${projectStyle}>
                        <div class="projects-calendar__project-background shadow-sm" ${projectStyleBackground}></div>
                        <div class="projects-calendar__project-description">
                            <span ${projectTextColor}>${project.title.replace(/\r?\n/g, "<br>")}</span>
                        </div>
                    </a>`
              );

              break;

            // Ostatni element
            case projectEndDateString:
              // Czas zakończenia dla składowej projektu jest mniejszy równy od czasu zakończenia widoku.
              if (projectEndMinutes <= headerEndMinutes) {
                projectClass = "projects-calendar__project--end";
                projectStyle = Tools.getStyleAttribute({
                  width: ((projectEndMinutes - headerStartMinutes) * 100) / (headerEndMinutes - headerStartMinutes) + "%",
                  "margin-left": 0,
                });
              }

              // Czas zakończenia dla składowej projektu jest większy od czasu rozpoczęcia widoku.
              if (projectEndMinutes > headerEndMinutes) {
                projectClass = "";
                projectStyle = Tools.getStyleAttribute({
                  "margin-left": 0,
                });
              }

              // Czas zakończenia dla składowej projektu jest mniejszy od czasu rozpoczęcia widoku.
              if (projectEndMinutes < headerStartMinutes) {
                projectClass = "";
                projectStyle = Tools.getStyleAttribute({
                  display: "none",
                });
              }

              // Tworzenie składowej projektu.
              projectsMap = Tools.mapSetStringDuplicateKeys(
                projectsMap,
                projectResourceId,
                `<a tabindex="0" ${href} class="projects-calendar__project ${projectClass}" data-id="${project.id}" ${projectStyle}>
                        <div class="projects-calendar__project-background shadow-sm" ${projectStyleBackground}></div>
                        <div class="projects-calendar__project-description">
                            <span ${projectTextColor}>${project.title.replace(/\r?\n/g, "<br>")}</span>
                        </div>
                    </a>`
              );

              break;

            // Środkowy element
            default:
              projectClass = "";
              projectStyle = Tools.getStyleAttribute({
                "margin-left": 0,
              });

              // Tworzenie składowej projektu.
              projectsMap = Tools.mapSetStringDuplicateKeys(
                projectsMap,
                projectResourceId,
                `<a tabindex="0" ${href} class="projects-calendar__project ${projectClass}" data-id="${project.id}" ${projectStyle}>
                        <div class="projects-calendar__project-background shadow-sm" ${projectStyleBackground}></div>
                        <div class="projects-calendar__project-description">
                            <span ${projectTextColor}>${project.title.replace(/\r?\n/g, "<br>")}</span>
                        </div>
                    </a>`
              );

              break;
          }

          projectStartDate = new Date(projectStartDate.getFullYear(), projectStartDate.getMonth(), projectStartDate.getDate() + 1);
        }
      }

      //
      i++;
    }

    //
    const { rows } = this.viewBodyContentData;
    let projectsCalendarRowDivLength = rows.length;
    let html = "";

    for (let i = 0; i < projectsCalendarRowDivLength; i++) {
      let resourceId = rows[i].toLocaleDateString();
      let rowProjects = projectsMap.has(resourceId) ? projectsMap.get(resourceId) : "";

      html += `<div class="projects-calendar__body-content-row" data-resource-id="${resourceId}">${rowProjects}</div>`;
    }

    this.calendar.querySelector(".projects-calendar__body-content").insertAdjacentHTML("beforeend", html);
  }

  renderProjectsOnTheCalendarArea() {
    const projectsCalendarViewStartDate = new Date(this.viewData[this.settings.projectsCalendarView].start);
    const projectsCalendarViewEndDate = new Date(this.viewData[this.settings.projectsCalendarView].end);
    let projectsCalendarViewStart = projectsCalendarViewStartDate.getTime() - projectsCalendarViewStartDate.getTimezoneOffset() * 60000;
    let projectsCalendarViewEnd = projectsCalendarViewEndDate.getTime() - projectsCalendarViewEndDate.getTimezoneOffset() * 60000;
    let diffProjectsCalendarViewEndStart = projectsCalendarViewEnd - projectsCalendarViewStart;
    let projectsMap = new Map();

    // Posortowanie powierzchni według daty: rosnąco.
    const projectsDataSort = this.projectsData.sort((a, b) => {
      let projectStartDateA = Date.parse(a.start.replace(/ /g, "T")) / 1000 / 60;
      let projectStartDateB = Date.parse(b.start.replace(/ /g, "T")) / 1000 / 60;

      return projectStartDateA - projectStartDateB;
    });

    // Przygotowanie danych powierzchni
    const resources = {};
    let projectsDataSortLength = projectsDataSort.length;

    for (let i = 0; i < projectsDataSortLength; i++) {
      const project = projectsDataSort[i];
      const startDate = new Date(Date.parse(project.start.replace(/ /g, "T")));
      const endDate = new Date(Date.parse(project.end.replace(/ /g, "T")));

      if (resources.hasOwnProperty(project.resourceId)) {
        resources[project.resourceId].push({
          className: project.className,
          borderColor: project.borderColor,
          color: project.color,
          end: endDate.getTime() - endDate.getTimezoneOffset() * 60000,
          id: project.id,
          objectId: project.objectId,
          resourceId: project.resourceId,
          start: startDate.getTime() - startDate.getTimezoneOffset() * 60000,
          textColor: project.textColor,
          title: project.title,
          type: project.type,
          url: project.url,
          row: 1,
        });
      } else {
        resources[project.resourceId] = [
          {
            className: project.className,
            borderColor: project.borderColor,
            color: project.color,
            end: endDate.getTime() - endDate.getTimezoneOffset() * 60000,
            id: project.id,
            objectId: project.objectId,
            resourceId: project.resourceId,
            start: startDate.getTime() - startDate.getTimezoneOffset() * 60000,
            textColor: project.textColor,
            title: project.title,
            type: project.type,
            url: project.url,
            row: 1,
          },
        ];
      }
    }

    // Wyznaczenie rzędu (grid-row) dla powierzchni składowej oraz renderowanie w kalendarzu.
    for (const key in resources) {
      if (Object.hasOwnProperty.call(resources, key)) {
        const area = resources[key];
        let areaLength = area.length;
        const insertedComponentsStartEndDate = [];

        for (let i = 0; i < areaLength; i++) {
          const currentComponent = area[i];
          let currentComponentRow = currentComponent.row;
          const collisionsInRows = {};

          if (insertedComponentsStartEndDate.length > 0) {
            for (let j = 0; j < insertedComponentsStartEndDate.length; j++) {
              const previousComponent = insertedComponentsStartEndDate[j];

              // Sprawdzamy czy początek aktualnej składowej zawiera się w poprzedniej składowej.
              collisionsInRows[previousComponent.row] = currentComponent.start >= previousComponent.start && currentComponent.start < previousComponent.end;
            }

            //
            for (const key in collisionsInRows) {
              if (Object.hasOwnProperty.call(collisionsInRows, key)) {
                const collision = collisionsInRows[key];

                if (collision) {
                  currentComponentRow = parseInt(key) + 1;
                } else {
                  currentComponentRow = parseInt(key);
                  break;
                }
              }
            }
          }

          // Wstawienie aktualnego komponentu do listy już wstawionych.
          insertedComponentsStartEndDate.push({
            start: currentComponent.start,
            end: currentComponent.end,
            row: currentComponentRow,
          });

          //
          resources[key][i].row = currentComponentRow;

          // Wyrenderowanie komponentu na kalendarzu.
          let projectStartDate = currentComponent.start;
          let projectEndDate = currentComponent.end;
          let diffProjectEndStart = projectEndDate - projectStartDate;
          let projectClass = "";
          let projectStyle = "";
          let projectBackground = (() => {
            if (currentComponent.hasOwnProperty("borderColor")) {
              return Tools.getStyleAttribute({
                "background-color": currentComponent.color,
                border: `1px solid ${currentComponent.borderColor}`,
              });
            } else {
              return Tools.getStyleAttribute({
                "background-color": currentComponent.color,
              });
            }
          })();
          let projectTextColor = Tools.getStyleAttribute({
            color: currentComponent.textColor,
          });
          let href = this.settings.projectsCalendarPopoverTrigger === "mouseenter" ? `href="${currentComponent.url}"` : ``;

          // Czas rozpoczęcia dla składowej projektu jest większy równy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest mniejszy równy od czasu zakończenia widoku.
          if (projectStartDate >= projectsCalendarViewStart && projectEndDate <= projectsCalendarViewEnd) {
            projectClass = "projects-calendar__project--start projects-calendar__project--end";
            projectStyle = Tools.getStyleAttribute({
              width: (diffProjectEndStart * 100) / diffProjectsCalendarViewEndStart + "%",
              "margin-left": ((projectStartDate - projectsCalendarViewStart) * 100) / diffProjectsCalendarViewEndStart + "%",
              "grid-row": currentComponent.row,
            });
          }

          // Czas rozpoczęcia dla składowej projektu jest mniejszy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest większy od czasu zakończenia widoku.
          if (projectStartDate < projectsCalendarViewStart && projectEndDate > projectsCalendarViewEnd) {
            projectStyle = "";
          }

          // Czas rozpoczęcia dla składowej projektu jest większy równy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest większy od czasu zakończenia widoku.
          if (projectStartDate >= projectsCalendarViewStart && projectEndDate > projectsCalendarViewEnd) {
            projectClass = "projects-calendar__project--start";
            projectStyle = Tools.getStyleAttribute({
              "margin-left": ((projectStartDate - projectsCalendarViewStart) * 100) / diffProjectsCalendarViewEndStart + "%",
              "grid-row": currentComponent.row,
            });
          }

          // Czas rozpoczęcia dla składowej projektu jest mniejszy od czasu rozpoczęcia widoku oraz czas zakończenia dla składowej projektu jest mniejszy równy od czasu zakończenia widoku.
          if (projectStartDate < projectsCalendarViewStart && projectEndDate <= projectsCalendarViewEnd) {
            projectClass = "projects-calendar__project--end";
            projectStyle = Tools.getStyleAttribute({
              width: ((projectEndDate - projectsCalendarViewStart) * 100) / diffProjectsCalendarViewEndStart + "%",
              "grid-row": currentComponent.row,
            });
          }

          //
          const selectorLi = this.calendar.querySelector('li[data-resource-id="' + currentComponent.resourceId + '"]');

          if (Boolean(selectorLi)) {
            selectorLi.style.setProperty("display", "inline-flex");
          }

          // Tworzenie składowej projektu.
          projectsMap = Tools.mapSetStringDuplicateKeys(
            projectsMap,
            currentComponent.resourceId,
            `<a tabindex="0" ${href} class="projects-calendar__project ${projectClass}" data-id="${currentComponent.id}" ${projectStyle}>
                <div class="projects-calendar__project-background shadow-sm" ${projectBackground}></div>
                <div class="projects-calendar__project-description">
                    <span ${projectTextColor}>${currentComponent.title.replace(/\r?\n/g, "<br>")}</span>
                </div>
            </a>`
          );
        }
      }
    }

    const { rows } = this.viewBodyContentData;
    let html = "";

    for (const key in rows) {
      if (Object.hasOwnProperty.call(rows, key)) {
        let areaId = rows[key].id;
        let rowProjects = projectsMap.has(areaId) ? projectsMap.get(areaId) : "";

        if (!this.settings.projectsCalendarHideEmptyResources) {
          html += `<div class="projects-calendar__body-content-row" data-resource-id="${areaId}">${rowProjects}</div>`;
        } else {
          if (rowProjects !== "") {
            html += `<div class="projects-calendar__body-content-row d-grid" data-resource-id="${areaId}">${rowProjects}</div>`;
          }
        }
      }
    }

    this.calendar.querySelector(".projects-calendar__body-content").insertAdjacentHTML("beforeend", html);
  }

  mouseenterProject(project) {
    [...this.calendar.querySelectorAll('[data-id="' + project.dataset.id + '"]')].forEach((project, index) => {
      project.classList.add("projects-calendar__project--selected");
    });
  }

  mouseleaveProject(project) {
    [...this.calendar.querySelectorAll('[data-id="' + project.dataset.id + '"]')].forEach((project, index) => {
      project.classList.remove("projects-calendar__project--selected");
    });
  }

  getResource() {
    return this.resources[this.settings.projectsCalendarView];
  }

  getResourceType() {
    return this.resources[this.settings.projectsCalendarView].type;
  }

  getGridInterval() {
    const { slotLabelInterval, slotDuration } = this.settings.projectsCalendarResources[this.settings.projectsCalendarView];
    let slotLabelMinutes = Tools.getMinutesFromTimeString(slotLabelInterval);
    let slotDurationMinutes = Tools.getMinutesFromTimeString(slotDuration);

    return (() => {
      if (slotLabelMinutes >= slotDurationMinutes) {
        if (slotLabelMinutes % slotDurationMinutes === 0) {
          return slotDurationMinutes;
        } else {
          return slotLabelMinutes;
        }
      } else {
        return slotLabelMinutes;
      }
    })();
  }

  getHours(interval, s) {
    const dataHours = [];
    let start = new Date(s);
    let end = new Date(s);

    if (this.settings.projectsCalendarBusinessHoursStart !== "") {
      start.setMinutes(Tools.getMinutesFromTimeString(this.settings.projectsCalendarBusinessHoursStart));
    } else {
      start.setHours(0, 0, 0);
    }

    if (this.settings.projectsCalendarBusinessHoursEnd !== "") {
      end.setMinutes(Tools.getMinutesFromTimeString(this.settings.projectsCalendarBusinessHoursEnd), 59);
    } else {
      end.setHours(23, 59, 59);
    }

    while (start < end) {
      dataHours.push(start);
      start = new Date(start.getFullYear(), start.getMonth(), start.getDate(), start.getHours(), start.getMinutes(), start.getSeconds() + Tools.getSecondsFromTimeString(interval));
    }

    return dataHours;
  }

  getProjectsPopover(event) {
    switch (event.type) {
      case "mouseenter":
      case "click":
        this.popoverTimeout = setTimeout(
          () => {
            const project = event.target.closest(".projects-calendar__project");

            const area = this.projectsData.filter((item) => {
              return project.dataset.id === item.id;
            })[0];

            let urlSearchParamsString = new URLSearchParams({
              type: area.type,
              id: area.objectId,
              start: area.start.split(" ")[0],
              end: area.end.split(" ")[0],
              "powierzchnia_id[]": area.resourceId,
            }).toString();

            // Inicjalizacja i pokazanie preloadera.
            const preloaderProject = new Preloader(project);
            preloaderProject.show();

            // Wysłanie żądania Ajax
            Ajax.sendAjaxRequest(
              `${this.settings.projectsCalendarUrlDetails}&${urlSearchParamsString}`,
              {
                method: "GET",
                headers: {
                  "X-Requested-With": "XMLHttpRequest",
                },
              },
              "String",
              false
            )
              .then((html) => {
                let trigger = "";

                switch (this.settings.projectsCalendarPopoverTrigger) {
                  case "click":
                    // Usunięcie zdarzenia, po utworzeniu instancji new bootstrap.Popover() zdarzenie już nie jest potrzebne. Cała obsługa jest realizowana po przez Popover.
                    project.removeEventListener("click", this.getProjectsPopoverBind);

                    //
                    trigger = "focus";
                    break;

                  case "mouseenter":
                    // Usunięcie zdarzenia, po utworzeniu instancji new bootstrap.Popover() zdarzenie już nie jest potrzebne. Cała obsługa jest realizowana po przez Popover.
                    project.removeEventListener("mouseenter", this.getProjectsPopoverBind);
                    project.removeEventListener("mouseleave", this.getProjectsPopoverBind);

                    //
                    trigger = "hover";
                    break;
                }

                this.popoverData.set(
                  project,
                  new bootstrap.Popover(project, {
                    content: html,
                    sanitize: false,
                    html: true,
                    container: this.calendar,
                    trigger: trigger,
                  })
                );

                // Usunięcie preloadera.
                preloaderProject.hide();
              })
              .catch((e) => console.log(e));
          },
          event.type === "click" ? 0 : this.settings.projectsCalendarPopoverTimeout
        );

        break;

      case "mouseleave":
        clearTimeout(this.popoverTimeout);
        break;

      default:
        break;
    }
  }

  setProjectsCalendarBodyModifierClass() {
    const projectsCalendarBody = this.calendar.querySelector(".projects-calendar__body");

    switch (this.getResourceType()) {
      case "date":
        projectsCalendarBody.classList.add("projects-calendar__body--date");
        projectsCalendarBody.classList.remove("projects-calendar__body--area");
        break;

      case "area":
        projectsCalendarBody.classList.add("projects-calendar__body--area");
        projectsCalendarBody.classList.remove("projects-calendar__body--date");
        break;

      default:
        break;
    }

    switch (this.getResourceType()) {
      case "date":
        projectsCalendarBody.classList.remove("projects-calendar__body--hide-empty-resources");
        break;

      case "area":
        if (this.settings.projectsCalendarHideEmptyResources) {
          projectsCalendarBody.classList.add("projects-calendar__body--hide-empty-resources");
        } else {
          projectsCalendarBody.classList.remove("projects-calendar__body--hide-empty-resources");
        }
        break;

      default:
        break;
    }
  }

  calculateProjectsCalendarBodyHeight() {
    const projectsCalendarBody = this.calendar.querySelector(".projects-calendar__body");

    if (this.settings.projectsCalendarFullScreenMode) {
      projectsCalendarBody.style.setProperty("height", "auto");
    } else {
      let windowInnerHeight = window.innerHeight;
      const calendarBoundingClientRect = this.calendar.getBoundingClientRect();

      projectsCalendarBody.style.setProperty("height", `${windowInnerHeight - calendarBoundingClientRect.top - 150}px`);
    }
  }

  calculateProjectsCalendarBodySidebarPaddingTop() {
    const projectsCalendarBodyHeader = this.calendar.querySelector(".projects-calendar__body-header");
    const projectsCalendarBodySidebar = this.calendar.querySelector(".projects-calendar__body-sidebar");

    projectsCalendarBodySidebar.style.setProperty("padding-top", `${projectsCalendarBodyHeader.getBoundingClientRect().height}px`);
  }

  calculateProjectsCalendarBodySidebarLiHeight() {
    const projectsCalendarRow = [...this.calendar.querySelectorAll(".projects-calendar__body-content-row:not(:empty)")];
    let projectsCalendarRowLength = projectsCalendarRow.length;
    let style = "";

    for (let i = 0; i < projectsCalendarRowLength; i++) {
      const element = projectsCalendarRow[i];
      let elementOffsetHeight = element.offsetHeight;
      let elementResourceId = element.dataset.resourceId;

      style += `.projects-calendar__body-sidebar li[data-resource-id="${elementResourceId}"]{height: ${elementOffsetHeight}px}`;
    }

    this.calendar.querySelector(".projects-calendar__body-sidebar").insertAdjacentHTML("afterbegin", `<style>${style}</style>`);
  }

  mousedownHandler(event) {
    switch (event.button) {
      case 0:
        if (event.target.closest(".projects-calendar__project")) {
          return false;
        }

        if (event.target.closest(".projects-calendar__body-content-row")) {
          //
          this.calendar.querySelector(".projects-calendar__body-content").addEventListener("mousemove", this.mousemoveHandlerBind);

          //
          const highlightedColumns = [...this.highlightedColumns];

          //
          this.highlightedColumnFirstIndex = [...this.calendar.querySelector(".projects-calendar__body-content-background").children].indexOf(
            document.elementsFromPoint(event.clientX, event.clientY).filter((item) => {
              return item.parentElement ? item.parentElement.classList.contains("projects-calendar__body-content-background") : false;
            })[0]
          );

          //
          this.highlightedColumnFirstWidth = [...this.calendar.querySelector(".projects-calendar__body-content-background").children][this.highlightedColumnFirstIndex].offsetWidth;

          //
          this.highlightedRow = event.target.closest(".projects-calendar__body-content-row");
          highlightedColumns.push(this.highlightedColumnFirstIndex);

          // Dodanie pierwszego zaznaczenia do kolumny
          this.addHighlightToColumn();

          //
          this.highlightedColumns = highlightedColumns;
        }

        break;
    }
  }

  mousemoveHandler(event) {
    switch (event.button) {
      case 0:
        let index = [...this.calendar.querySelector(".projects-calendar__body-content-background").children].indexOf(
          document.elementsFromPoint(event.clientX, event.clientY).filter((item) => {
            return item.parentElement ? item.parentElement.classList.contains("projects-calendar__body-content-background") : false;
          })[0]
        );
        const highlightedColumns = [];

        // Przesuwamy kursor myszy na prawo od pierwszej zaznaczonej składowej powierzchni.
        if (index > this.highlightedColumnFirstIndex) {
          //
          this.highlightedColumnDirection = "right";

          //
          for (let i = this.highlightedColumnFirstIndex; i <= index; i++) {
            highlightedColumns.push(i);
          }

          this.highlightedColumns = highlightedColumns;
        }

        //
        if (index === this.highlightedColumnFirstIndex) {
          this.highlightedColumns = [this.highlightedColumnFirstIndex];
        }

        // Przesuwamy kursor myszy na lewo od pierwszej zaznaczonej składowej powierzchni.
        if (index < this.highlightedColumnFirstIndex) {
          //
          this.highlightedColumnDirection = "left";

          //
          for (let i = this.highlightedColumnFirstIndex; i >= index; i--) {
            highlightedColumns.push(i);
          }

          this.highlightedColumns = highlightedColumns.reverse();
        }

        break;
    }
  }

  mouseupHandler(event) {
    switch (event.button) {
      case 0:
        //
        this.calendar.querySelector(".projects-calendar__body-content").removeEventListener("mousemove", this.mousemoveHandlerBind);

        if (this.highlightedRow) {
          const { slotLabelInterval, slotDuration } = this.settings.projectsCalendarResources[this.settings.projectsCalendarView];
          let slotLabelMinutes = Tools.getMinutesFromTimeString(slotLabelInterval);
          let slotDurationMinutes = Tools.getMinutesFromTimeString(slotDuration);
          let firstHighlightedColumn = this.highlightedColumns[0];
          let lastHighlightedColumn = this.highlightedColumns[this.highlightedColumns.length - 1];
          const { columns, rows } = this.viewBodyContentData;
          const dataFrom = columns[firstHighlightedColumn];
          const dataTo = columns[lastHighlightedColumn];
          let from = dataFrom;
          let to = (() => {
            if (slotLabelMinutes >= slotDurationMinutes) {
              if (slotLabelMinutes % slotDurationMinutes === 0) {
                return new Date(dataTo.getFullYear(), dataTo.getMonth(), dataTo.getDate(), dataTo.getHours(), dataTo.getMinutes() + slotDurationMinutes);
              } else {
                return new Date(dataTo.getFullYear(), dataTo.getMonth(), dataTo.getDate(), dataTo.getHours(), dataTo.getMinutes() + slotLabelMinutes);
              }
            } else {
              return new Date(dataTo.getFullYear(), dataTo.getMonth(), dataTo.getDate(), dataTo.getHours(), dataTo.getMinutes() + slotLabelMinutes);
            }
          })();

          // Wywołanie zdarzenia po wyborze daty na elemencie kalendarza.
          this.calendar.dispatchEvent(
            new CustomEvent("projectscalendarselect", {
              bubbles: true,
              detail: {
                instance: this,
                calendar: this.calendar,
                highlightedRow: this.highlightedRow,
                highlightedColumns: this.highlightedColumns,
                highlightedDate: {
                  from: from,
                  to: to,
                },
              },
            })
          );

          // Reset ustawień podświetlenia.
          this.highlightedColumnFirstIndex = null;
          this.highlightedColumnFirstWidth = null;
          this.highlightedColumnDirection = null;
          this.highlightedRow = null;
          this.highlightedColumns = [];
          this.removeHighlightFromColumn();
        }

        break;
    }
  }

  addHighlightToColumn() {
    this.highlightedRow.insertAdjacentHTML("afterbegin", `<div class="projects-calendar__highlight"></div>`);
  }

  removeHighlightFromColumn() {
    this.calendar.querySelector(".projects-calendar__highlight").remove();
  }

  calcWidthOfHighlight() {
    let css = `width: ${this.highlightedColumnFirstWidth * this.highlightedColumns.length}px;`;
    let allColumns = [...this.calendar.querySelector(".projects-calendar__body-content-background").children].length;

    switch (this.highlightedColumnDirection) {
      case "right":
        css += `
              left: ${this.highlightedColumnFirstWidth * this.highlightedColumnFirstIndex}px;
              right: auto;`;
        break;

      case "left":
        css += `
              left: auto;
              right: ${(allColumns - (this.highlightedColumnFirstIndex + 1)) * this.highlightedColumnFirstWidth}px;`;
        break;

      default:
        css += `
              left: ${this.highlightedColumnFirstWidth * this.highlightedColumnFirstIndex}px;
              right: auto;`;
        break;
    }

    this.calendar.querySelector(".projects-calendar__highlight").style.cssText = css;
  }

  init() {
    // Dodanie zdarzeń.
    this.addEvents();

    // Przygotowanie przycisków.
    this.buttons = this.settings.projectsCalendarButtons;
    this.buttons = this.settings.projectsCalendarCustomButtons;

    // Przygotowanie widoku.
    this.resources = this.settings.projectsCalendarResources;

    // Przygotowanie danych.
    this.calculateViewData();
    this.calculateViewBodySidebarData();
    this.calculateViewBodyHeaderData();
    this.calculateViewBodyContentData();

    // Renderowanie kalendarza.
    this.renderView();

    // Utworzenie Map.
    iksoris.instances = iksoris.instances || new Map();

    if (!Boolean(iksoris.instances.get(this.calendar.id))) {
      iksoris.instances.set(this.calendar.id, this);
    }

    // Dodanie styli użytkownika
    Tools.addStylesheetRules(this.calendar.id + "-stylesheet", this.settings.projectsCalendarCustomStylesheetRules);
  }
}

export { ProjectsCalendar };
